/**
 * Collection page
 *
 * Handles:
 * - Filter drawer open / close with focus trap & a11y
 * - AJAX filtering with dynamic "Show x results" button
 * - Sort select
 * - Infinite scroll (auto), load-more button, or classic pagination
 * - URL updates (?page=x, filter params, sort_by)
 */

(function () {
  'use strict';

  var section = document.querySelector('.section-collection');
  if (!section) return;

  var SECTION_ID  = section.dataset.sectionId;
  var LOAD_MODE   = section.dataset.loadMore || 'auto_scroll';

  var isLoading     = false;
  var _prevFocused  = null;

  /* ── helpers ───────────────────────────────────────────────── */

  function getSelectedFiltersCount(form) {
    if (!form) return 0;
    var count = 0;
    var checkboxes = form.querySelectorAll('input[type="checkbox"]:checked');
    count += checkboxes.length;
    var priceBlock = form.querySelector('.collection-filter-price');
    if (priceBlock) {
      var ranges = priceBlock.querySelectorAll('input[type="range"]');
      if (ranges.length >= 2) {
        var minVal = parseFloat(ranges[0].value) || 0;
        var maxVal = parseFloat(ranges[1].value) || 0;
        var rangeMax = parseFloat(ranges[1].max) || 1000;
        if (minVal > 0 || maxVal < rangeMax) {
          count += 1;
        }
      }
    }
    return count;
  }

  function updateDrawerTitleCount(form) {
    var drawer = getFilterDrawer();
    if (!drawer) return;
    var titleEl = drawer.querySelector('.collection-filter-drawer-title');
    if (!titleEl) return;
    var count = getSelectedFiltersCount(form);
    var counterSpan = titleEl.querySelector('.drawer-filter-counter');
    if (count > 0) {
      if (counterSpan) {
        counterSpan.textContent = '(' + count + ')';
      } else {
        counterSpan = document.createElement('span');
        counterSpan.className = 'drawer-filter-counter';
        counterSpan.textContent = '(' + count + ')';
        titleEl.appendChild(document.createTextNode(' '));
        titleEl.appendChild(counterSpan);
      }
    } else {
      if (counterSpan) {
        counterSpan.remove();
      }
    }
  }

  function updateToggleCounter(form) {
    var liveToggle = section.querySelector('.collection-filter-toggle');
    if (!liveToggle) return;
    var count = getSelectedFiltersCount(form);
    var liveCounter = liveToggle.querySelector('.filter-toggle-counter');
    if (count > 0) {
      if (liveCounter) {
        liveCounter.textContent = '(' + count + ')';
      } else {
        var span = document.createElement('span');
        span.className = 'filter-toggle-counter';
        span.textContent = '(' + count + ')';
        var svg = liveToggle.querySelector('svg');
        if (svg) {
          liveToggle.insertBefore(span, svg);
        } else {
          liveToggle.appendChild(span);
        }
      }
    } else {
      if (liveCounter) {
        liveCounter.remove();
      }
    }
  }

  function updateURL(url, replace) {
    if (replace) {
      window.history.replaceState({}, '', url);
    } else {
      window.history.pushState({}, '', url);
    }
  }

  /**
   * Fetch a section using the Sections Rendering API (?sections=).
   * This preserves template-assigned section settings unlike ?section_id=.
   * Returns a parsed Document from the section HTML.
   */
  function fetchSectionDoc(url) {
    var separator = url.indexOf('?') > -1 ? '&' : '?';
    var fetchUrl  = url + separator + 'sections=' + SECTION_ID;

    return fetch(fetchUrl)
      .then(function (res) { return res.json(); })
      .then(function (json) {
        var html = json[SECTION_ID];
        if (!html) throw new Error('Section not found in response');
        return new DOMParser().parseFromString(html, 'text/html');
      });
  }

  /* ── Section replacement (for filter / sort / popstate) ───── */

  function replaceSection(doc) {
    var newSection = doc.querySelector('.section-collection');
    if (newSection && section.parentNode) {

      var isDrawerOpen = document.body.classList.contains('open-filter');
      
      // Track open accordion categories and scroll position before replacement
      var openGroupLabels = [];
      var scrollPos = 0;
      var drawer = getFilterDrawer();
      if (drawer) {
        var groups = drawer.querySelectorAll('.collection-filter-group');
        groups.forEach(function (group) {
          if (group.classList.contains('is-open')) {
            var labelEl = group.querySelector('.collection-filter-group__title span');
            if (labelEl) {
              var labelText = labelEl.textContent.replace(/\(\d+\)/, '').trim();
              openGroupLabels.push(labelText);
            }
          }
        });
        var scrollEl = drawer.querySelector('.collection-filter-form-data');
        if (scrollEl) {
          scrollPos = scrollEl.scrollTop;
        }
      }

       // Pre-set drawer state in the new section fragment to prevent transition/flicker upon insertion
      if (isDrawerOpen) {
        var newDrawer = newSection.querySelector('.collection-filter-drawer');
        if (newDrawer) {
          newDrawer.setAttribute('aria-hidden', 'false');
        }
      }

      section.parentNode.replaceChild(newSection, section);
      section = newSection;

      // Restore open groups before running initAccordion so it computes styles correctly
      if (openGroupLabels.length > 0) {
        var newGroups = section.querySelectorAll('.collection-filter-group');
        newGroups.forEach(function (group) {
          var labelEl = group.querySelector('.collection-filter-group__title span');
          if (labelEl) {
            var labelText = labelEl.textContent.replace(/\(\d+\)/, '').trim();
            if (openGroupLabels.indexOf(labelText) > -1 && isFilterGroupActive(group)) {
              group.classList.add('is-open');
              var title = group.querySelector('.collection-filter-group__title');
              if (title) {
                title.setAttribute('aria-expanded', 'true');
              }
            }
          }
        });
      }

      if (LOAD_MODE === 'auto_scroll') {
        initAutoScroll();
      }

      if (typeof quickAddButton === 'function') {
        quickAddButton();
      }
      if (typeof initProductCardSliders === 'function') {
        initProductCardSliders();
      }
      initAccordion();
      initPriceSliders();
      initStickyObserver();

      // Restore drawer state if it was open
      if (isDrawerOpen) {
        var newDrawer = getFilterDrawer();
        if (newDrawer) {
          newDrawer.setAttribute('aria-hidden', 'false');
          if (scrollPos > 0) {
            var newScrollEl = newDrawer.querySelector('.collection-filter-form-data');
            if (newScrollEl) {
              newScrollEl.scrollTop = scrollPos;
            }
          }
        }
      }

      // Dispatch event to re-initialize section-specific scripts (e.g. sub-collection slider)
      document.dispatchEvent(new CustomEvent('collection:reloaded', {
        detail: { sectionId: SECTION_ID }
      }));
    }
  }

  /* ── Filter drawer ─────────────────────────────────────────── */

  function getFilterDrawer() {
    return section.querySelector('.collection-filter-drawer');
  }

  function isFilterGroupActive(group) {
    // Check if any checkbox is checked
    var checkedCheckboxes = group.querySelectorAll('input[type="checkbox"]:checked');
    if (checkedCheckboxes.length > 0) {
      return true;
    }

    // Check if price range is active (min > 0 or max < rangeMax)
    var priceSlider = group.querySelector('.price-range-slider');
    if (priceSlider) {
      var minInput = priceSlider.querySelector('.min-range');
      var maxInput = priceSlider.querySelector('.max-range');
      var rangeMax = parseFloat(priceSlider.dataset.rangeMax) || 1000;
      if (minInput && maxInput) {
        var minVal = parseFloat(minInput.value) || 0;
        var maxVal = parseFloat(maxInput.value) || rangeMax;
        if (minVal > 0 || maxVal < rangeMax) {
          return true;
        }
      }
    }

    return false;
  }

  function openFilterDrawer() {
    var drawer = getFilterDrawer();
    if (!drawer) return;

    // Open any group that has active filters
    var groups = drawer.querySelectorAll('.collection-filter-group');
    groups.forEach(function (group) {
      if (isFilterGroupActive(group)) {
        group.classList.add('is-open');
        var title = group.querySelector('.collection-filter-group__title');
        if (title) title.setAttribute('aria-expanded', 'true');
        var body = group.querySelector('.collection-filter-group__body');
        if (body) {
          body.style.height = 'auto';
          body.style.overflow = 'visible';
        }
      }
    });

    _prevFocused = document.activeElement;
    drawer.setAttribute('aria-hidden', 'false');
    document.body.classList.add('open-filter');
    drawer.focus();
  }

  function closeFilterDrawer() {
    var drawer = getFilterDrawer();
    document.body.classList.remove('open-filter');
    if (drawer) {
      drawer.setAttribute('aria-hidden', 'true');
    }
    if (_prevFocused && _prevFocused.focus) _prevFocused.focus();
  }

  /* ── Availability injection ───────────────────────────────── */

  /**
   * When any filter is active, automatically add filter.v.availability=1
   * so only in-stock items are requested.
   */
  function injectAvailabilityForSize(params) {
    var hasFilter = false;
    params.forEach(function (value, key) {
      if (key.indexOf('filter.') === 0 && key !== 'filter.v.availability') {
        hasFilter = true;
      }
    });
    if (hasFilter) {
      params.set('filter.v.availability', '1');
    }
  }

  /* ── Dynamic "Show x results" on filter changes ────────────── */

  function updateResultCount() {
    var drawer = getFilterDrawer();
    if (!drawer) return;

    var form = drawer.querySelector('.collection-filter-form');
    if (!form) return;

    updateDrawerTitleCount(form);
    updateToggleCounter(form);

    var formData = new FormData(form);
    var params   = new URLSearchParams();

    for (var pair of formData.entries()) {
      if (pair[1]) params.append(pair[0], pair[1]);
    }

    var priceBlock = form.querySelector('.collection-filter-price');
    if (priceBlock) {
      var ranges = priceBlock.querySelectorAll('input[type="range"]');
      if (ranges.length >= 2) {
        var minVal = parseFloat(ranges[0].value);
        var maxVal = parseFloat(ranges[1].value);
        var rangeMax = parseFloat(ranges[1].max);
        
        if (minVal === 0 && maxVal === rangeMax) {
          var hiddens = priceBlock.querySelectorAll('input[type="hidden"]');
          hiddens.forEach(function(input) {
            params.delete(input.name);
          });
        }
      }
    }

    var currentSort = new URLSearchParams(window.location.search).get('sort_by');
    if (currentSort) {
      params.set('sort_by', currentSort);
    }

    injectAvailabilityForSize(params);

    var fetchUrl = form.dataset.collectionUrl + '?' + params.toString();

    fetchSectionDoc(fetchUrl)
      .then(function (doc) {
        var newSection = doc.querySelector('.section-collection');
        if (!newSection) return;

        var count = newSection.dataset.productsCount || '0';
        var applyBtn = drawer.querySelector('.collection-filter-apply');
        var newApplyBtn = newSection.querySelector('.collection-filter-apply');
        if (applyBtn && newApplyBtn) {
          applyBtn.innerHTML = newApplyBtn.innerHTML;
        }

        var clearBtn = drawer.querySelector('.collection-filter-clear');
        var newClearBtn = newSection.querySelector('.collection-filter-clear');
        if (clearBtn && newClearBtn) {
          if (newClearBtn.hasAttribute('disabled')) {
            clearBtn.setAttribute('disabled', 'disabled');
          } else {
            clearBtn.removeAttribute('disabled');
          }
        }
        var clearBtn = drawer.querySelector('.collection-filter-clear');
        var newClearBtn = newSection.querySelector('.collection-filter-clear');
        if (clearBtn && newClearBtn) {
          if (newClearBtn.hasAttribute('disabled')) {
            clearBtn.setAttribute('disabled', 'disabled');
          } else {
            clearBtn.removeAttribute('disabled');
          }
        }
        // 1. Update the Active Filters block
        var currentActiveWrapper = drawer.querySelector('.collection-filter-drawer-active-wrapper');
        var newActiveWrapper = newSection.querySelector('.collection-filter-drawer-active-wrapper');
        
        if (newActiveWrapper) {
          if (currentActiveWrapper) {
            currentActiveWrapper.outerHTML = newActiveWrapper.outerHTML;
          } else {
            // Insert it above the first group
            var formDataContainer = drawer.querySelector('.collection-filter-form-data');
            var firstGroup = formDataContainer.querySelector('.collection-filter-group');
            if (firstGroup) {
              formDataContainer.insertBefore(newActiveWrapper, firstGroup);
            }
          }
        } else {
          if (currentActiveWrapper) {
            currentActiveWrapper.remove();
          }
        }

        // 2. Update the Group Title Spans (for active counts)
        var currentTitles = drawer.querySelectorAll('.collection-filter-group__title > span');
        var newTitles = newSection.querySelectorAll('.collection-filter-group__title > span');
        
        for (var i = 0; i < currentTitles.length; i++) {
          if (newTitles[i]) {
            currentTitles[i].innerHTML = newTitles[i].innerHTML;
          }
        }
        // 4. Update the filter lists (checkboxes, disabled states)
        var currentLists = drawer.querySelectorAll('.collection-filter-list');
        var newLists = newSection.querySelectorAll('.collection-filter-list');
        for (var i = 0; i < currentLists.length; i++) {
          if (newLists[i]) {
            currentLists[i].innerHTML = newLists[i].innerHTML;
          }
        }

        // 4. Update the collection products grid
        var currentGrid = section.querySelector('.collection-grid');
        var newGrid = newSection.querySelector('.collection-grid');
        if (currentGrid && newGrid) {
          currentGrid.outerHTML = newGrid.outerHTML;
        }

        // 5. Update the navigation/pagination
        var currentPagination = section.querySelector('.collection-pagination');
        var newPagination = newSection.querySelector('.collection-pagination');
        if (currentPagination && newPagination) {
          currentPagination.outerHTML = newPagination.outerHTML;
        }

        // 6. Update active filters block in sticky bar
        var currentActiveBar = section.querySelector('.collection-active-filters');
        var newActiveBar = newSection.querySelector('.collection-active-filters');
        if (currentActiveBar && newActiveBar) {
          currentActiveBar.outerHTML = newActiveBar.outerHTML;
        } else if (newActiveBar) {
          var stickyBar = section.querySelector('.collection-sticky-bar');
          if (stickyBar) {
            stickyBar.appendChild(newActiveBar);
          }
        } else if (currentActiveBar) {
          currentActiveBar.remove();
        }

        // Update dataset properties
        section.dataset.productsCount = newSection.dataset.productsCount || '0';
        section.dataset.totalPages = newSection.dataset.totalPages || '1';
        section.dataset.currentPage = newSection.dataset.currentPage || '1';

        // Update browser URL (preserve state, replace history page index parameters)
        updateURL(fetchUrl, true);

        // Re-initialize JavaScript bindings for new product card items
        if (typeof quickAddButton === 'function') {
          quickAddButton();
        }
        if (typeof initProductCardSliders === 'function') {
          initProductCardSliders();
        }
        if (LOAD_MODE === 'auto_scroll') {
          initAutoScroll();
        }
      })
      .catch(console.error);
  }
   function updateClearAllButtonState(form) {
    if (!form) return;
    var clearBtn = form.querySelector('.collection-filter-clear');
    if (!clearBtn) return;

    var isActive = false;

    // Check checkboxes
    var checkboxes = form.querySelectorAll('input[type="checkbox"]');
    for (var i = 0; i < checkboxes.length; i++) {
      if (checkboxes[i].checked) {
        isActive = true;
        break;
      }
    }

    // Check price ranges
    if (!isActive) {
      var priceBlock = form.querySelector('.collection-filter-price');
      if (priceBlock) {
        var ranges = priceBlock.querySelectorAll('input[type="range"]');
        if (ranges.length >= 2) {
          var minVal = parseFloat(ranges[0].value);
          var maxVal = parseFloat(ranges[1].value);
          var rangeMax = parseFloat(ranges[1].max);
          if (minVal > 0 || maxVal < rangeMax) {
            isActive = true;
          }
        }
      }
    }

    if (isActive) {
      clearBtn.removeAttribute('disabled');
    } else {
      clearBtn.setAttribute('disabled', 'disabled');
    }
  }

  function clearAllFilters(form) {
    if (!form) return;

    // Uncheck all checkboxes
    var checkboxes = form.querySelectorAll('input[type="checkbox"]');
    checkboxes.forEach(function (cb) {
      cb.checked = false;
    });

    // Reset price range inputs
    var priceBlock = form.querySelector('.collection-filter-price');
    if (priceBlock) {
      var hiddens = priceBlock.querySelectorAll('input[type="hidden"]');
      hiddens.forEach(function(input) {
        input.value = "";
      });
      
      var ranges = priceBlock.querySelectorAll('input[type="range"]');
      if (ranges.length >= 2) {
        ranges[0].value = 0;
        var maxVal = ranges[1].getAttribute('max') || 1000;
        ranges[1].value = maxVal;
        ranges[0].dispatchEvent(new Event('input', { bubbles: true }));
        ranges[1].dispatchEvent(new Event('input', { bubbles: true }));
      }
      
      var textInputs = priceBlock.querySelectorAll('input[type="text"]');
      textInputs.forEach(function(input) {
        input.blur();
      });
    }

    // Close all open accordions
    var groups = form.querySelectorAll('.collection-filter-group');
    groups.forEach(function (group) {
      if (group.classList.contains('is-open')) {
        var body = group.querySelector('.collection-filter-group__body');
        var title = group.querySelector('.collection-filter-group__title');
        if (body) {
          if (body.style.height === 'auto' || body.style.height === '') {
            body.style.height = body.scrollHeight + 'px';
            body.offsetHeight;
          }
          body.style.height = '0px';
          body.style.overflow = 'hidden';
        }
        group.classList.remove('is-open');
        if (title) title.setAttribute('aria-expanded', 'false');
      }
    });

    // Update Clear All button state (will disable it)
    updateClearAllButtonState(form);

    // Update URL and product grid
    var params = new URLSearchParams();
    
    // Keep sort_by if present
    var currentSort = new URLSearchParams(window.location.search).get('sort_by');
    if (currentSort) {
      params.set('sort_by', currentSort);
    }

    var newUrl = form.dataset.collectionUrl + '?' + params.toString();

    updateURL(newUrl);

    fetchSectionDoc(newUrl)
      .then(function (doc) {
        replaceSection(doc);
      })
      .catch(console.error);
  }

  /* ── Clear all and button state helper functions ────────────── */

  function updateClearAllButtonState(form) {
    if (!form) return;
    var clearBtn = form.querySelector('.collection-filter-clear');
    if (!clearBtn) return;

    var isActive = false;

    // Check checkboxes
    var checkboxes = form.querySelectorAll('input[type="checkbox"]');
    for (var i = 0; i < checkboxes.length; i++) {
      if (checkboxes[i].checked) {
        isActive = true;
        break;
      }
    }

    // Check price ranges
    if (!isActive) {
      var priceBlock = form.querySelector('.collection-filter-price');
      if (priceBlock) {
        var ranges = priceBlock.querySelectorAll('input[type="range"]');
        if (ranges.length >= 2) {
          var minVal = parseFloat(ranges[0].value);
          var maxVal = parseFloat(ranges[1].value);
          var rangeMax = parseFloat(ranges[1].max);
          if (minVal > 0 || maxVal < rangeMax) {
            isActive = true;
          }
        }
      }
    }

    if (isActive) {
      clearBtn.removeAttribute('disabled');
    } else {
      clearBtn.setAttribute('disabled', 'disabled');
    }
  }

  function clearAllFilters(form) {
    if (!form) return;

    // Uncheck all checkboxes
    var checkboxes = form.querySelectorAll('input[type="checkbox"]');
    checkboxes.forEach(function (cb) {
      cb.checked = false;
    });

    // Reset price range inputs
    var priceBlock = form.querySelector('.collection-filter-price');
    if (priceBlock) {
      var hiddens = priceBlock.querySelectorAll('input[type="hidden"]');
      hiddens.forEach(function(input) {
        input.value = "";
      });
      
      var ranges = priceBlock.querySelectorAll('input[type="range"]');
      if (ranges.length >= 2) {
        ranges[0].value = 0;
        var maxVal = ranges[1].getAttribute('max') || 1000;
        ranges[1].value = maxVal;
        ranges[0].dispatchEvent(new Event('input', { bubbles: true }));
        ranges[1].dispatchEvent(new Event('input', { bubbles: true }));
      }
      
      var textInputs = priceBlock.querySelectorAll('input[type="text"]');
      textInputs.forEach(function(input) {
        input.blur();
      });
    }

    // Close all open accordions
    var groups = form.querySelectorAll('.collection-filter-group');
    groups.forEach(function (group) {
      if (group.classList.contains('is-open')) {
        var body = group.querySelector('.collection-filter-group__body');
        var title = group.querySelector('.collection-filter-group__title');
        if (body) {
          if (body.style.height === 'auto' || body.style.height === '') {
            body.style.height = body.scrollHeight + 'px';
            body.offsetHeight;
          }
          body.style.height = '0px';
          body.style.overflow = 'hidden';
        }
        group.classList.remove('is-open');
        if (title) title.setAttribute('aria-expanded', 'false');
      }
    });

    // Update Clear All button state (will disable it)
    updateClearAllButtonState(form);

    // Update URL and product grid
    var params = new URLSearchParams();
    
    // Keep sort_by if present
    var currentSort = new URLSearchParams(window.location.search).get('sort_by');
    if (currentSort) {
      params.set('sort_by', currentSort);
    }

    var newUrl = form.dataset.collectionUrl + '?' + params.toString();

    updateURL(newUrl);

    fetchSectionDoc(newUrl)
      .then(function (doc) {
        replaceSection(doc);
      })
      .catch(console.error);
  }


  /* ── Filter form submit ────────────────────────────────────── */

  function handleFilterSubmit(e) {
    e.preventDefault();
    var form     = e.target;
    updateToggleCounter(form);
    var formData = new FormData(form);
    var params   = new URLSearchParams();

    for (var pair of formData.entries()) {
      if (pair[1]) params.append(pair[0], pair[1]);
    }

    var priceBlock = form.querySelector('.collection-filter-price');
    if (priceBlock) {
      var ranges = priceBlock.querySelectorAll('input[type="range"]');
      if (ranges.length >= 2) {
        var minVal = parseFloat(ranges[0].value);
        var maxVal = parseFloat(ranges[1].value);
        var rangeMax = parseFloat(ranges[1].max);
        
        if (minVal === 0 && maxVal === rangeMax) {
          var hiddens = priceBlock.querySelectorAll('input[type="hidden"]');
          hiddens.forEach(function(input) {
            params.delete(input.name);
          });
        }
      }
    }

    var currentSort = new URLSearchParams(window.location.search).get('sort_by');
    if (currentSort) {
      params.set('sort_by', currentSort);
    }

    injectAvailabilityForSize(params);

    var newUrl = form.dataset.collectionUrl + '?' + params.toString();

    // Push the state to history on submit so the final selection is undoable via back-button
    updateURL(newUrl, false);

    closeFilterDrawer();
    window.scrollTo({ top: 0, behavior: 'smooth' });
  }

  /* ── Sort ──────────────────────────────────────────────────── */

  function handleSortValue(value) {
    var url = new URL(window.location.href);
    url.searchParams.set('sort_by', value);
    url.searchParams.delete('page');

    updateURL(url.toString());

    fetchSectionDoc(url.toString())
      .then(function (doc) {
        replaceSection(doc);
      })
      .catch(console.error);
  }

  /* ── Load more / infinite scroll ───────────────────────────── */

  function loadNextPage(loadMoreEl) {
    if (isLoading || !loadMoreEl) return;

    var nextUrl = loadMoreEl.getAttribute('href');
    if (!nextUrl) return;

    isLoading = true;
    loadMoreEl.classList.add('is-loading');

    // Create and append skeleton cards
    var productGrid = section.querySelector('.collection-grid');
    var skeletons = [];
    if (productGrid) {
      for (var i = 0; i < 4; i++) {
        var skel = document.createElement('div');
        skel.className = 'product-item product-skeleton';
        skel.setAttribute('role', 'listitem');
        skel.innerHTML = 
          '<div class="snippet-product-card skeleton-card">' +
            '<div class="grid-imagebox skeleton-image"></div>' +
            '<div class="skeleton-meta">' +
              '<div class="skeleton-text"></div>' +
              '<div class="skeleton-text short"></div>' +
            '</div>' +
          '</div>';
        productGrid.appendChild(skel);
        skeletons.push(skel);
      }
    }

    fetchSectionDoc(nextUrl)
      .then(function (doc) {
        // Remove skeletons
        skeletons.forEach(function (skel) {
          skel.remove();
        });

        var newGrid = doc.querySelector('.collection-grid');

        /* Append new products to the live grid */
        if (newGrid && productGrid) {
          var items = newGrid.querySelectorAll('.product-item');
          for (var i = 0; i < items.length; i++) {
            productGrid.appendChild(items[i]);
          }
        }

        /* Check if there is another page after the one we just loaded */
        var nextLink = doc.querySelector('[data-load-more]');
        var hasMore  = false;

        if (nextLink && nextLink.getAttribute('href')) {
          loadMoreEl.setAttribute('href', nextLink.getAttribute('href'));
          hasMore = true;
        } else {
          /* Fallback: use section data attributes to determine next page */
          var newSection = doc.querySelector('.section-collection');
          if (newSection) {
            var totalPages  = parseInt(newSection.dataset.totalPages) || 0;
            var currentPage = parseInt(newSection.dataset.currentPage) || 0;
            if (currentPage < totalPages) {
              var nextPageUrl = new URL(nextUrl, window.location.origin);
              nextPageUrl.searchParams.set('page', currentPage + 1);
              loadMoreEl.setAttribute('href', nextPageUrl.pathname + nextPageUrl.search);
              hasMore = true;
            }
          }
        }

        if (hasMore) {
          loadMoreEl.style.display = '';
        } else {
          /* No more pages — hide the trigger */
          loadMoreEl.style.display = 'none';
          if (autoScrollObserver) autoScrollObserver.disconnect();
        }

        /* Update "Page X of Y" text (load_more_btn mode) */
        var liveInfo = section.querySelector('.collection-pagination-info');
        var newInfo  = doc.querySelector('.collection-pagination-info');
        if (liveInfo && newInfo) {
          liveInfo.innerHTML = newInfo.innerHTML;
        } else if (liveInfo && !hasMore) {
          /* Last page reached — the fetched doc won't contain pagination-info
             because paginate.next is falsy, so update manually */
          var totalPages = section.dataset.totalPages || '?';
          liveInfo.textContent = liveInfo.textContent.replace(/\d+(\s*(?:of|\/)\s*)\d+/, totalPages + '$1' + totalPages);
        }

        /* Re-bind quick add */
        if (typeof quickAddButton === 'function') {
          quickAddButton();
        }
        if (typeof initProductCardSliders === 'function') {
          initProductCardSliders();
        }

        isLoading = false;
        loadMoreEl.classList.remove('is-loading');
      })
      .catch(function (err) {
        console.error('Load more error:', err);
        skeletons.forEach(function (skel) {
          skel.remove();
        });
        isLoading = false;
        loadMoreEl.classList.remove('is-loading');
      });
  }

  /* ── Auto scroll observer ──────────────────────────────────── */

  var autoScrollObserver = null;
  var isAutoScrollIntersecting = false;

  function initAutoScroll() {
    if (autoScrollObserver) autoScrollObserver.disconnect();

    var loadMoreEl = section.querySelector('.collection-load-more--auto[data-load-more]');
    if (!loadMoreEl) return;

    var hasScrolled = false;
    isAutoScrollIntersecting = false;

    window.addEventListener('scroll', function() {
      hasScrolled = true;
      if (isAutoScrollIntersecting && !isLoading) {
        loadNextPage(loadMoreEl);
      }
    }, { once: true });

    autoScrollObserver = new IntersectionObserver(function (entries) {
      entries.forEach(function (entry) {
        isAutoScrollIntersecting = entry.isIntersecting;
        if (entry.isIntersecting && !isLoading) {
          if (hasScrolled || window.scrollY > 0) {
            loadNextPage(loadMoreEl);
          }
        }
      });
    }, { rootMargin: '0px 0px 200px 0px' });

    autoScrollObserver.observe(loadMoreEl);
  }

  /* ── Delegated event listeners ─────────────────────────────── */

  /* Overlay click closes filter (capture phase to bypass cart-drawer stopPropagation) */
  var overlay = document.querySelector('.overlay-box');
  if (overlay) {
    overlay.addEventListener('click', function () {
      if (document.body.classList.contains('open-filter')) {
        closeFilterDrawer();
      }
    }, true);
  }

  document.addEventListener('click', function (e) {
    /* Toggle sort dropdown */
    var sortToggle = e.target.closest('.collection-sort-toggle-btn');
    if (sortToggle) {
      e.preventDefault();
      var dropdown = sortToggle.closest('.collection-sort-dropdown');
      if (dropdown) {
        var isOpen = dropdown.classList.contains('is-open');
        document.querySelectorAll('.collection-sort-dropdown').forEach(function(d) {
          d.classList.remove('is-open');
          var btn = d.querySelector('.collection-sort-toggle-btn');
          if (btn) btn.setAttribute('aria-expanded', 'false');
        });
        if (!isOpen) {
          dropdown.classList.add('is-open');
          sortToggle.setAttribute('aria-expanded', 'true');
        }
      }
      return;
    }

    /* Select sort option */
    var sortItem = e.target.closest('.collection-sort-item');
    if (sortItem) {
      e.preventDefault();
      var val = sortItem.dataset.value;
      if (val) {
        handleSortValue(val);
      }
      var dropdown = sortItem.closest('.collection-sort-dropdown');
      if (dropdown) {
        dropdown.classList.remove('is-open');
        var btn = dropdown.querySelector('.collection-sort-toggle-btn');
        if (btn) btn.setAttribute('aria-expanded', 'false');
      }
      return;
    }

    /* Close sort dropdown when clicking outside */
    var openDropdowns = document.querySelectorAll('.collection-sort-dropdown.is-open');
    if (openDropdowns.length > 0 && !e.target.closest('.collection-sort-dropdown')) {
      openDropdowns.forEach(function(d) {
        d.classList.remove('is-open');
        var btn = d.querySelector('.collection-sort-toggle-btn');
        if (btn) btn.setAttribute('aria-expanded', 'false');
      });
    }

    /* Open filter drawer */
    if (e.target.closest('.collection-filter-toggle')) {
      openFilterDrawer();
      return;
    }

    /* Close filter drawer */
    if (e.target.closest('.collection-filter-close')) {
      closeFilterDrawer();
      return;
    }

    /* Clear All button click inside drawer */
    var clearAllBtn = e.target.closest('.collection-filter-clear');
    if (clearAllBtn) {
      e.preventDefault();
      var form = clearAllBtn.closest('.collection-filter-form');
      if (form) {
        clearAllFilters(form);
      }
      return;
    }

    /* Clear All button click inside drawer */
    var clearAllBtn = e.target.closest('.collection-filter-clear');
    if (clearAllBtn) {
      e.preventDefault();
      var form = clearAllBtn.closest('.collection-filter-form');
      if (form) {
        clearAllFilters(form);
      }
      return;
    }

    /* Click on drawer backdrop (outside inner panel) */
    var drawer = getFilterDrawer();
    if (drawer && document.body.classList.contains('open-filter') && e.target === drawer) {
      closeFilterDrawer();
      return;
    }

    /* Load more button */
    var loadMoreBtn = e.target.closest('.collection-load-more--btn[data-load-more]');
    if (loadMoreBtn) {
      e.preventDefault();
      loadNextPage(loadMoreBtn);
      return;
    }

    /* Active tag click on page (sticky bar) */
    var pageActiveTag = e.target.closest('.collection-active-tag');
    if (pageActiveTag) {
      e.preventDefault();
      var href = pageActiveTag.getAttribute('href');
      if (href) {
        updateURL(href);
        fetchSectionDoc(href)
          .then(function (doc) {
            replaceSection(doc);
          })
          .catch(console.error);
      }
      return;
    }

    /* Active tag click inside drawer */
    // var activeTag = e.target.closest('.collection-filter-drawer-active-tag');
    // if (activeTag) {
    //   e.preventDefault();
    //   var drawer = getFilterDrawer();
    //   if (!drawer) return;
    //   var form = drawer.querySelector('.collection-filter-form');
    //   if (!form) return;

    //   if (activeTag.dataset.priceFilter) {
    //     var priceBlock = form.querySelector('.collection-filter-price');
    //     if (priceBlock) {
    //       var hiddens = priceBlock.querySelectorAll('input[type="hidden"]');
    //       hiddens.forEach(function(input) {
    //         input.value = "";
    //       });
          
    //       var ranges = priceBlock.querySelectorAll('input[type="range"]');
    //       if (ranges.length >= 2) {
    //         ranges[0].value = 0;
    //         ranges[1].value = ranges[1].max || 1000;
    //         ranges[0].dispatchEvent(new Event('input', { bubbles: true }));
    //         ranges[1].dispatchEvent(new Event('input', { bubbles: true }));
    //       }
          
    //       var textInputs = priceBlock.querySelectorAll('input[type="text"]');
    //       textInputs.forEach(function(input) {
    //         input.blur();
    //       });
    //     }
    //   } else {
    //     var name = activeTag.dataset.name;
    //     var val = activeTag.dataset.value;
    //     var checkboxes = form.querySelectorAll('input[name="' + name + '"]');
    //     for (var i = 0; i < checkboxes.length; i++) {
    //       if (checkboxes[i].value === val) {
    //         checkboxes[i].checked = false;
    //         break;
    //       }
    //     }
    //   }

    //   form.dispatchEvent(new Event('change', { bubbles: true }));
    //   return;
    // }

    var shopBtn = e.target.closest('.shop-look-button');
    if (shopBtn) {
      e.preventDefault();
      e.stopImmediatePropagation();

      var card = shopBtn.closest('.snippet-product-card');
      if (card) {
        var template = card.querySelector('.mobile-aside-template');
        if (template) {
          var shopModal = shopBtn.getAttribute('data-modal');
          document.body.classList.add('open-shop-look');
          document.getElementById(shopModal).getElementsByClassName('shop-look-popup')[0].innerHTML = template.innerHTML;
          quickAddButton();
          return;
        }
      }

      var shopUrl   = shopBtn.getAttribute('data-url');
      var shopModal = shopBtn.getAttribute('data-modal');
      fetch(shopUrl)
        .then(function (response) { return response.text(); })
        .then(function (response) {
          var html   = new DOMParser().parseFromString(response, 'text/html');
          var source = html.getElementById('section-shop-look');
          document.body.classList.add('open-shop-look');
          document.getElementById(shopModal).getElementsByClassName('shop-look-popup')[0].innerHTML = source.innerHTML;
          quickAddButton();
        })
        .catch(function (err) { console.error(err); });
      return;
    }
  });

  /* Escape key closes filter */
  document.addEventListener('keydown', function (e) {
    if (e.key === 'Escape' && document.body.classList.contains('open-filter')) {
      closeFilterDrawer();
      return;
    }

    if (e.key === 'Escape' && document.body.classList.contains('open-shop-look')) {
      document.body.classList.remove('open-shop-look');
      return;
    }

    /* Focus trap inside filter drawer */
    if (e.key === 'Tab' && document.body.classList.contains('open-filter')) {
      var drawer = getFilterDrawer();
      if (!drawer) return;

      var focusable = drawer.querySelectorAll(
        'a[href], button:not([disabled]), input:not([disabled]), select:not([disabled]), textarea:not([disabled]), [tabindex]:not([tabindex="-1"]), details > summary'
      );
      if (!focusable.length) return;

      var first = focusable[0];
      var last  = focusable[focusable.length - 1];

      if (e.shiftKey) {
        if (document.activeElement === first) { e.preventDefault(); last.focus(); }
      } else {
        if (document.activeElement === last) { e.preventDefault(); first.focus(); }
      }
    }
  });

  /* Filter form change */
document.addEventListener('change', function (e) {
    if (e.target.closest('.collection-filter-form')) {
      if (e.target.type === 'checkbox') {
        var group = e.target.closest('.collection-filter-group');
        if (group) {
          var anyChecked = group.querySelectorAll('input[type="checkbox"]:checked').length > 0;
          if (anyChecked) {
            group.classList.add('has-active-filter');
            // Ensure the group stays open
            if (!group.classList.contains('is-open')) {
              var title = group.querySelector('.collection-filter-group__title');
              var body = group.querySelector('.collection-filter-group__body');
              group.classList.add('is-open');
              if (title) title.setAttribute('aria-expanded', 'true');
              if (body) { body.style.height = 'auto'; body.style.overflow = 'visible'; }
            }
          } else {
            group.classList.remove('has-active-filter');

            // Close the accordion if it has no selections
            if (group.classList.contains('is-open')) {
              var body = group.querySelector('.collection-filter-group__body');
              var title = group.querySelector('.collection-filter-group__title');
              if (body) {
                if (body.style.height === 'auto' || body.style.height === '') {
                  body.style.height = body.scrollHeight + 'px';
                  body.offsetHeight;
                }
                body.style.height = '0px';
                body.style.overflow = 'hidden';
              }
              group.classList.remove('is-open');
              if (title) title.setAttribute('aria-expanded', 'false');
            }
          }
        }
      }
      updateResultCount();
    }
  });


  /* Filter range inputs */
  document.addEventListener('input', function (e) {
    if (e.target.closest('.collection-filter-price input') && e.target.type !== 'range') {
      clearTimeout(e.target._debounce);
      e.target._debounce = setTimeout(updateResultCount, 400);
    }
  });

  /* Filter form submit */
  document.addEventListener('submit', function (e) {
    if (e.target.closest('.collection-filter-form')) {
      handleFilterSubmit(e);
    }
  });

  /* Browser back/forward */
  window.addEventListener('popstate', function () {
    fetchSectionDoc(window.location.href)
      .then(function (doc) {
        replaceSection(doc);
      })
      .catch(console.error);
  });

  /* ── Accordion logic ───────────────────────────────────────── */

  function initAccordion() {
    var groups = section.querySelectorAll('.collection-filter-group');
    groups.forEach(function (group) {
      var title = group.querySelector('.collection-filter-group__title');
      var body = group.querySelector('.collection-filter-group__body');
      if (!title || !body) return;

      // Check if group is initially open OR has active filters
      var hasActiveFilters = isFilterGroupActive(group);
            if (hasActiveFilters) group.classList.add('has-active-filter');
      else group.classList.remove('has-active-filter');
      var isInitiallyOpen = group.classList.contains('is-open') || hasActiveFilters;
      if (isInitiallyOpen) {
        group.classList.add('is-open');
        title.setAttribute('aria-expanded', 'true');
        body.style.height = 'auto';
        body.style.overflow = 'visible';
      } else {
        group.classList.remove('is-open');
        title.setAttribute('aria-expanded', 'false');
        body.style.height = '0px';
        body.style.overflow = 'hidden';
      }

      title.addEventListener('click', function () {
        var isOpen = group.classList.contains('is-open');

        // Toggle current group
        if (isOpen) {
          // If it was auto, set it to scrollHeight first so it has a pixel value to animate from
          if (body.style.height === 'auto' || body.style.height === '') {
            body.style.height = body.scrollHeight + 'px';
            body.offsetHeight; // force reflow
          }
          body.style.height = '0px';
          body.style.overflow = 'hidden';
          group.classList.remove('is-open');
          title.setAttribute('aria-expanded', 'false');
        } else {
          // Close all other groups when opening one
          groups.forEach(function (otherGroup) {
             if (otherGroup !== group && otherGroup.classList.contains('is-open') && !otherGroup.classList.contains('has-active-filter')) {
              var otherBody = otherGroup.querySelector('.collection-filter-group__body');
              var otherTitle = otherGroup.querySelector('.collection-filter-group__title');
              if (otherBody) {
                // Set to scrollHeight first to animate down from it
                if (otherBody.style.height === 'auto' || otherBody.style.height === '') {
                  otherBody.style.height = otherBody.scrollHeight + 'px';
                  otherBody.offsetHeight; // force reflow
                }
                otherBody.style.height = '0px';
                otherBody.style.overflow = 'hidden';
              }
              otherGroup.classList.remove('is-open');
              if (otherTitle) otherTitle.setAttribute('aria-expanded', 'false');
            }
          });

          group.classList.add('is-open');
          title.setAttribute('aria-expanded', 'true');
          body.style.height = body.scrollHeight + 'px';
          body.style.overflow = 'hidden'; // Keep hidden during opening transition
          
          // Once the transition finishes, set height to 'auto' so it behaves responsively
          function onTransitionEnd() {
            if (group.classList.contains('is-open')) {
              body.style.height = 'auto';
              body.style.overflow = 'visible';
            }
            body.removeEventListener('transitionend', onTransitionEnd);
          }
          body.addEventListener('transitionend', onTransitionEnd);
        }
      });
    });
  }

  /* ── Price range slider logic ──────────────────────────────── */

  function initPriceSliders() {
    var priceSliders = section.querySelectorAll('.price-range-slider');
    priceSliders.forEach(function (slider) {
      var minInput = slider.querySelector('.min-range');
      var maxInput = slider.querySelector('.max-range');
      var track = slider.querySelector('.price-range-track');
      if (!minInput || !maxInput || !track) return;

      var rangeMax = parseFloat(slider.dataset.rangeMax) || 1000;

      var fieldsWrapper = slider.closest('.collection-filter-price').querySelector('.collection-filter-price-filelds');
      if (!fieldsWrapper) return;

      var minField = fieldsWrapper.querySelectorAll('.collection-filter-price__field input:not([type="hidden"])')[0];
      var maxField = fieldsWrapper.querySelectorAll('.collection-filter-price__field input:not([type="hidden"])')[1];
      var minHidden = fieldsWrapper.querySelectorAll('.collection-filter-price__field input[type="hidden"]')[0];
      var maxHidden = fieldsWrapper.querySelectorAll('.collection-filter-price__field input[type="hidden"]')[1];
      if (!minField || !maxField || !minHidden || !maxHidden) return;

      function formatCurrency(value, placeholder) {
        var match = placeholder.match(/^[^0-9]+/);
        var prefix = match ? match[0] : '';
        return prefix + Math.round(value);
      }

      function parseNumber(str) {
        var clean = str.replace(/[^0-9.]/g, '');
        return parseFloat(clean) || 0;
      }

      function updateTrack() {
        var val1 = parseFloat(minInput.value);
        var val2 = parseFloat(maxInput.value);

        var percent1 = (val1 / rangeMax) * 100;
        var percent2 = (val2 / rangeMax) * 100;
        track.style.background = 'linear-gradient(to right, rgba(114, 118, 127, 0.5) ' + percent1 + '%, #000000 ' + percent1 + '%, #000000 ' + percent2 + '%, rgba(114, 118, 127, 0.5) ' + percent2 + '%)';
        
        minField.value = formatCurrency(val1, minField.placeholder);
        maxField.value = formatCurrency(val2, maxField.placeholder);
        minHidden.value = Math.round(val1);
        maxHidden.value = Math.round(val2);
      }

      function updateFromFields() {
        var val1 = parseNumber(minField.value);
        var val2 = parseNumber(maxField.value);

        if (val1 < 0) val1 = 0;
        if (val2 > rangeMax) val2 = rangeMax;
        if (val1 > val2) val1 = val2;

        minInput.value = val1;
        maxInput.value = val2;
        minHidden.value = Math.round(val1);
        maxHidden.value = Math.round(val2);

        var percent1 = (val1 / rangeMax) * 100;
        var percent2 = (val2 / rangeMax) * 100;
        track.style.background = 'linear-gradient(to right, rgba(114, 118, 127, 0.5) ' + percent1 + '%, #000000 ' + percent1 + '%, #000000 ' + percent2 + '%, rgba(114, 118, 127, 0.5) ' + percent2 + '%)';
      }

      minInput.addEventListener('input', function () {
        var val1 = parseFloat(minInput.value);
        var val2 = parseFloat(maxInput.value);
        if (val1 > val2) {
          minInput.value = val2;
        }
        updateTrack();
        minField.focus();
      });

      maxInput.addEventListener('input', function () {
        var val1 = parseFloat(minInput.value);
        var val2 = parseFloat(maxInput.value);
        if (val2 < val1) {
          maxInput.value = val1;
        }
        updateTrack();
        maxField.focus();
      });

      minInput.addEventListener('change', function () {
        var form = slider.closest('.collection-filter-form');
        if (form) {
          form.dispatchEvent(new Event('change', { bubbles: true }));
        }
      });
      maxInput.addEventListener('change', function () {
        var form = slider.closest('.collection-filter-form');
        if (form) {
          form.dispatchEvent(new Event('change', { bubbles: true }));
        }
      });

      minField.addEventListener('input', function() {
        updateFromFields();
      });
      maxField.addEventListener('input', function() {
        updateFromFields();
      });

      minField.addEventListener('blur', function() {
        var val = parseNumber(minField.value);
        minField.value = formatCurrency(val, minField.placeholder);
      });
      maxField.addEventListener('blur', function() {
        var val = parseNumber(maxField.value);
        maxField.value = formatCurrency(val, maxField.placeholder);
      });

      slider.addEventListener('click', function (e) {
        if (e.target === minInput || e.target === maxInput) return;

        var rect = slider.getBoundingClientRect();
        var clickX = e.clientX - rect.left;
        var percentage = clickX / rect.width;
        if (percentage < 0) percentage = 0;
        if (percentage > 1) percentage = 1;

        var clickedVal = percentage * rangeMax;

        var val1 = parseFloat(minInput.value);
        var val2 = parseFloat(maxInput.value);

        var diff1 = Math.abs(val1 - clickedVal);
        var diff2 = Math.abs(val2 - clickedVal);

        if (diff1 < diff2) {
          if (clickedVal > val2) clickedVal = val2;
          minInput.value = Math.round(clickedVal);
        } else {
          if (clickedVal < val1) clickedVal = val1;
          maxInput.value = Math.round(clickedVal);
        }

        updateTrack();

        var form = slider.closest('.collection-filter-form');
        if (form) {
          form.dispatchEvent(new Event('change', { bubbles: true }));
        }
      });

      if (minHidden.value !== "") {
        minInput.value = parseFloat(minHidden.value) || 0;
      }
      if (maxHidden.value !== "") {
        maxInput.value = parseFloat(maxHidden.value) || rangeMax;
      }
      updateTrack();
    });
  }

  /* ── Sticky observer logic ─────────────────────────────────── */

  function initStickyObserver() {
    var bar = section.querySelector('.collection-sticky-bar');
    if (!bar) return;

    var sentinel = section.querySelector('.collection-sticky-sentinel');
    if (!sentinel) {
      sentinel = document.createElement('div');
      sentinel.className = 'collection-sticky-sentinel';
      bar.parentNode.insertBefore(sentinel, bar);
    }

    var topMargin = 0;
    var headerSticky = document.querySelector('.main-header:has(.header-sticky)');
    if (headerSticky) {
      topMargin = window.innerWidth > 991 ? 64 : 50;
    }

    var observer = new IntersectionObserver(function (entries) {
      entries.forEach(function (entry) {
        if (!entry.isIntersecting) {
          bar.classList.add('is-stuck');
        } else {
          bar.classList.remove('is-stuck');
        }
      });
    }, {
      rootMargin: '-' + (topMargin + 1) + 'px 0px 0px 0px'
    });

    observer.observe(sentinel);
  }

  /* ── Init ──────────────────────────────────────────────────── */

  if (LOAD_MODE === 'auto_scroll') {
    initAutoScroll();
  }
  initAccordion();
  initPriceSliders();
  initStickyObserver();

  /* ── Scroll-direction header hide (collection page only) ─────── */

  (function initScrollHideHeader() {
    var mainHeader = document.querySelector('.main-header');
    if (!mainHeader) return;

    var lastScrollY = window.scrollY;
    var ticking     = false;
    var THRESHOLD   = 5; // px – ignore tiny jitter

    function onScroll() {
      if (!ticking) {
        window.requestAnimationFrame(function () {
          var currentY = window.scrollY;
          var diff     = currentY - lastScrollY;

          if (Math.abs(diff) < THRESHOLD) {
            ticking = false;
            return;
          }

          if (diff > 0 && currentY > 60) {
            // Scrolling DOWN — hide header
            mainHeader.classList.add('header-scroll-hidden');
          } else {
            // Scrolling UP — show header
            mainHeader.classList.remove('header-scroll-hidden');
          }

          lastScrollY = currentY;
          ticking     = false;
        });
        ticking = true;
      }
    }

    window.addEventListener('scroll', onScroll, { passive: true });
  })();

  /* ── Collection description: read more ──────────────────────── */

  (function initReadMore() {
    var wrappers = section.querySelectorAll('[data-read-more]');
    if (!wrappers.length) return;

    // Show the toggle only when the clamped content actually overflows 4 lines
    function update(wrapper) {
      if (wrapper.classList.contains('is-expanded')) return;
      var content = wrapper.querySelector('.read-more__content');
      var toggle  = wrapper.querySelector('.read-more__toggle');
      if (!content || !toggle) return;
      toggle.hidden = content.scrollHeight <= content.clientHeight + 1;
    }

    Array.prototype.forEach.call(wrappers, function (wrapper) {
      var toggle = wrapper.querySelector('.read-more__toggle');
      if (!toggle) return;

      toggle.addEventListener('click', function () {
        var expanded = wrapper.classList.toggle('is-expanded');
        toggle.setAttribute('aria-expanded', expanded ? 'true' : 'false');
        toggle.textContent = expanded ? toggle.dataset.less : toggle.dataset.more;
      });

      update(wrapper);

      // Re-check when width changes (breakpoints, font load, rotation)
      if ('ResizeObserver' in window) {
        new ResizeObserver(function () { update(wrapper); }).observe(wrapper);
      }
    });
  })();

})();

